"use client";

// Dynamic import to avoid SSR issues
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let Adjust: any = null;

// Adjust configuration
const ADJUST_CONFIG = {
  appToken: "728c5ystp2ps",
  environment: "production" as const,
  // Enable verbose logging in development
  logLevel: process.env.NODE_ENV === "development" ? ("verbose" as const) : ("error" as const),
};

// Event tokens for different download types
export const ADJUST_EVENT_TOKENS = {
  download: "2k4dba", // General download event
  downloadOnly: "j4q1ju", // Download only (direct download)
  downloadMacApple: "l5riku", // Mac Apple Silicon download
  downloadMacIntel: "bzxp82", // Mac Intel download
  downloadWindows: "lxjylv", // Windows download
  /* 只上报子域名的数据 */
  subDownload: "gizj11",
  subDownloadOnly: "ff04rn",
  subDownloadMacApple: "a31pf1",
  subDownloadMacIntel: "aen6ip",
  subDownloadWindows: "wgj3go",
} as const;

// Load Adjust SDK dynamically
const loadAdjustSDK = async () => {
  if (Adjust) return Adjust;

  try {
    const AdjustModule = await import("@adjustcom/adjust-web-sdk");
    Adjust = AdjustModule.default;
    return Adjust;
  } catch (error) {
    console.error("Failed to load Adjust SDK:", error);
    return null;
  }
};

// Track initialization state
let isInitialized = false;
let initPromise: Promise<void> | null = null;

// Initialize the Adjust SDK
export const initializeAdjust = async () => {
  // Only initialize on client side
  if (typeof window === "undefined") return;
  if (isInitialized) return;
  if (initPromise) return initPromise;

  initPromise = (async () => {
    try {
      const AdjustSDK = await loadAdjustSDK();
      if (!AdjustSDK) return;

      AdjustSDK.initSdk(ADJUST_CONFIG);
      isInitialized = true;
      console.log("Adjust SDK initialized successfully");
    } catch (error) {
      console.error("Failed to initialize Adjust SDK:", error);
    }
  })();

  return initPromise;
};

// Generic function to track events
const trackAdjustEvent = async (eventToken: string, eventData?: Record<string, string>) => {
  // Only track on client side
  if (typeof window === "undefined") return;

  try {
    // Ensure SDK is initialized before tracking
    if (!isInitialized) {
      console.log("Waiting for Adjust SDK initialization...");
      await initializeAdjust();
    }

    const AdjustSDK = await loadAdjustSDK();
    if (!AdjustSDK) {
      console.error("Adjust SDK not available for tracking");
      return;
    }

    const eventConfig: { eventToken: string; callbackParameters?: Record<string, string> } = { eventToken };

    // Add callback parameters if provided
    if (eventData) {
      eventConfig.callbackParameters = eventData;
    }

    AdjustSDK.trackEvent(eventConfig);
    console.log(`Adjust event tracked: ${eventToken}`, eventData);
  } catch (error) {
    console.error("Failed to track Adjust event:", error);
  }
};

// Specific tracking functions for different download events
export const adjustTracking = {
  // Track general download event (modal opens)
  trackDownload: (data?: Record<string, string>) => {
    trackAdjustEvent(ADJUST_EVENT_TOKENS.download, data);
  },

  // Track direct download without form
  trackDownloadOnly: (data?: Record<string, string>) => {
    trackAdjustEvent(ADJUST_EVENT_TOKENS.downloadOnly, data);
  },

  // Track Mac Apple Silicon download
  trackMacAppleDownload: (data?: Record<string, string>) => {
    trackAdjustEvent(ADJUST_EVENT_TOKENS.downloadMacApple, data);
  },

  // Track Mac Intel download
  trackMacIntelDownload: (data?: Record<string, string>) => {
    trackAdjustEvent(ADJUST_EVENT_TOKENS.downloadMacIntel, data);
  },

  // Track Windows download
  trackWindowsDownload: (data?: Record<string, string>) => {
    trackAdjustEvent(ADJUST_EVENT_TOKENS.downloadWindows, data);
  },

  /* 只上报子域名的数据 */
  // 子域名下载事件
  trackSubDownload: (data?: Record<string, string>) => {
    trackAdjustEvent(ADJUST_EVENT_TOKENS.subDownload, data);
  },

  // 子域名下载事件
  trackSubDownloadOnly: (data?: Record<string, string>) => {
    trackAdjustEvent(ADJUST_EVENT_TOKENS.subDownloadOnly, data);
  },

  // 子域名Mac Apple Silicon下载事件
  trackSubDownloadMacApple: (data?: Record<string, string>) => {
    trackAdjustEvent(ADJUST_EVENT_TOKENS.subDownloadMacApple, data);
  },

  // 子域名Mac Intel下载事件
  trackSubDownloadMacIntel: (data?: Record<string, string>) => {
    trackAdjustEvent(ADJUST_EVENT_TOKENS.subDownloadMacIntel, data);
  },

  // 子域名Windows下载事件
  trackSubDownloadWindows: (data?: Record<string, string>) => {
    trackAdjustEvent(ADJUST_EVENT_TOKENS.subDownloadWindows, data);
  },
};

// Export the loadAdjustSDK function for advanced usage if needed
export { loadAdjustSDK };
